/**
 * Workout Templates List Page (Admin) - Epic 3
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Dumbbell,
  Calendar,
  Trash2,
  Edit2,
  Loader2,
  Copy,
  MoreVertical,
} from "lucide-react";
import { useWorkoutTemplates, deleteWorkoutTemplate } from "../hooks/useWorkout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Toaster, toast } from "sonner";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function WorkoutTemplatesPage() {
  const navigate = useNavigate();
  const { templates, isLoading, mutate } = useWorkoutTemplates();
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  
  const handleDelete = async () => {
    if (!deleteTemplateId) return;
    
    try {
      await deleteWorkoutTemplate(deleteTemplateId);
      toast.success("Programme supprimé");
      mutate();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleteTemplateId(null);
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950">
      <Toaster position="top-right" richColors />
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/admin")}
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <h1 className="font-heading text-xl font-bold text-white">
                MES PROGRAMMES
              </h1>
            </div>
            
            <Button
              onClick={() => navigate("/admin/templates/new")}
              data-testid="create-template-button"
              className="h-10 px-4 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
          </div>
        ) : templates.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-16 text-center">
              <Dumbbell className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400 mb-2">Aucun programme créé</p>
              <p className="text-zinc-600 text-sm mb-6">
                Créez votre premier programme d'entraînement
              </p>
              <Button
                onClick={() => navigate("/admin/templates/new")}
                className="bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer un programme
              </Button>
            </CardContent>
          </Card>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {templates.map((template) => (
              <motion.div key={template.id} variants={itemVariants}>
                <Card
                  className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/admin/templates/${template.id}`)}
                  data-testid={`template-card-${template.id}`}
                >
                  <CardContent className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-lime-400/10 rounded-xl flex items-center justify-center">
                        <Dumbbell className="w-6 h-6 text-lime-400" />
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-zinc-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-zinc-900 border-zinc-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            className="text-zinc-300 focus:text-white focus:bg-zinc-800"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/templates/${template.id}`);
                            }}
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTemplateId(template.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Content */}
                    <h3 className="font-semibold text-white text-lg mb-1">
                      {template.name}
                    </h3>
                    
                    {template.description && (
                      <p className="text-zinc-500 text-sm mb-3 line-clamp-2">
                        {template.description}
                      </p>
                    )}
                    
                    {/* Stats */}
                    <div className="flex items-center gap-3 text-sm">
                      <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                        {template.exercises_count} exercice{template.exercises_count > 1 ? "s" : ""}
                      </Badge>
                      <span className="text-zinc-600 text-xs flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(template.created_at).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>
      
      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTemplateId}
        onOpenChange={() => setDeleteTemplateId(null)}
      >
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Supprimer ce programme ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Cette action est irréversible. Le programme sera supprimé définitivement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
